/**
 * T-0117 / C20-131 — New-engagement wizard state shape + validation.
 *
 * The wizard is a step state machine carried in a single React state
 * object. The shape is shared between the wizard host and every step pane
 * so the steps stay presentational — each step takes (state, patch) and
 * decides what to render.
 *
 * The first two steps are the same for every engagement
 * (`design_handoff_caliber_v2/prototype/modals-wizard.js` minus its fifth
 * step); the modules chosen on step 2 decide how the wizard ends
 * (`wizardStepsFor`):
 *
 *   1. client-project   — pick client (or create new inline) + name the
 *                          project, set go-live, engagement lead, client PM.
 *   2. modules          — card grid (Contract Compliance, Health Check,
 *                          Go-Live Readiness, Selection, Discovery).
 *
 *   With an IV&V module on (Contract Compliance, Health Check or Go-Live
 *   Readiness) the engagement has a contract baseline to form, so:
 *
 *   3. baseline-docs    — drop / browse SOW, change orders, interviews,
 *                          plans. Real chunked upload via T-0040; status
 *                          from the documents list (T-0159).
 *   4. base-formation   — the AI extraction run (T-0047), started once
 *                          every uploaded document is indexed; live poll of
 *                          the run until it settles; the last step.
 *
 *   With only Discovery and/or Selection on there is no SOW to read and no
 *   base-formation run to start (the API refuses one; see
 *   `usesContractBaseline`), so one closing step replaces those two:
 *
 *   3. next-steps       — where to start for the modules chosen: the
 *                          Discovery Set up screen, the Selection
 *                          requirements, the Documents screen for uploads.
 *
 * The prototype's fifth step, the methodology proposal review, is gone
 * (E20 / T-0228): assessment defaults apply at creation (T-0219) and
 * nothing waits on a methodology any more. Proposals are decided in the AI
 * review queue after the wizard closes.
 *
 * Commit timing — the engagement is minted at the END of step 2 (Modules
 * → Continue). The later steps operate on the live engagement so uploads
 * and the extraction run are real backend writes. The final "Open
 * engagement" button on the last step just closes + navigates — the
 * engagement already exists; the user has been driving it since step 3.
 *
 * Why a separate module:
 *   - The host coordinator (`new-engagement-wizard.tsx`) and every step
 *     file import from here, which keeps circular-dep risk to zero.
 *   - Validation gates live next to the shape so step components don't
 *     re-implement "is this step satisfiable yet?" logic on their own.
 *   - The cross-cutting standards (§2) require the create POSTs go through
 *     `Idempotency-Key`; the wizard mints one per step that writes and
 *     keeps it stable across re-renders so a network retry doesn't double
 *     a write — that idempotency key fingerprint also lives here.
 */

import type { DocumentParty } from '@/lib/api/documents';

/** Wizard cursor: the two shared steps, then either closing flow. */
export type WizardStepId =
  | 'client-project'
  | 'modules'
  | 'baseline-docs'
  | 'base-formation'
  | 'next-steps';

export interface WizardStep {
  id: WizardStepId;
  num: number;
  name: string;
  sub: string;
}

const SHARED_STEPS: ReadonlyArray<WizardStep> = [
  { id: 'client-project', num: 1, name: 'Client & project', sub: 'Pick client, name engagement' },
  {
    id: 'modules',
    num: 2,
    name: 'Modules',
    sub: 'Compliance · Health · Go-Live · Selection · Discovery',
  },
];

/** The contract-baseline flow: the wizard as it has always ended. */
export const WIZARD_STEPS: ReadonlyArray<WizardStep> = [
  ...SHARED_STEPS,
  { id: 'baseline-docs', num: 3, name: 'Baseline docs', sub: 'SOW, plan, interviews' },
  { id: 'base-formation', num: 4, name: 'AI base formation', sub: 'Caliber reads + proposes' },
];

/** The flow for an engagement with no IV&V module: one closing step. */
export const NEXT_STEPS_WIZARD_STEPS: ReadonlyArray<WizardStep> = [
  ...SHARED_STEPS,
  { id: 'next-steps', num: 3, name: 'Next steps', sub: 'Where to start' },
];

/**
 * Whether the engagement has a contract baseline to form, from its module
 * switches: true when an IV&V module is on (Contract Compliance, Health
 * Check or Go-Live Readiness), false for a Discovery and/or Selection-only
 * engagement.
 *
 * Health Check and Go-Live Readiness without Contract Compliance keep the
 * document steps on purpose. The E21 design table lists the baseline finder
 * under contract_compliance, but the shipped routing table
 * (`packages/ai-workers/src/registry.ts`, T-0233) deliberately leaves it
 * ungated: the baseline run also forms the register framework, and ongoing
 * routing (risks and issues from status reports, no module switch) waits
 * for a finished baseline run on every engagement. A Health-Check-only
 * engagement with no run would never get those proposals. The API's run
 * guard (`apps/api/src/services/base-formation.ts`) and the worker's auto
 * path apply the same rule, so the three agree.
 */
export function usesContractBaseline(
  modules: Pick<ModulesStepState, 'contract' | 'health' | 'golive'>,
): boolean {
  return Boolean(modules.contract || modules.health || modules.golive);
}

/** The steps the wizard runs for these modules (`WIZARD_STEPS` or the next-steps flow). */
export function wizardStepsFor(modules: ModulesStepState): ReadonlyArray<WizardStep> {
  return usesContractBaseline(modules) ? WIZARD_STEPS : NEXT_STEPS_WIZARD_STEPS;
}

/**
 * A saved draft may carry a step the wizard no longer has (the removed
 * review step), a closing step of the other flow (a draft left on the
 * documents step whose modules now have no IV&V module), or nothing
 * usable: start it at the first step then. A draft is only ever saved
 * before the engagement exists, so nothing is lost by starting over.
 */
export function coerceWizardStep(step: unknown, modules?: ModulesStepState): WizardStepId {
  const steps = modules ? wizardStepsFor(modules) : WIZARD_STEPS;
  const found = steps.find((s) => s.id === step);
  return found ? found.id : 'client-project';
}

export const CLIENT_TYPES = [
  { value: 'city', label: 'City' },
  { value: 'county', label: 'County' },
  { value: 'state_agency', label: 'State agency' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Combined Client + Project step state — matches the prototype's `stepClient()`
 * (modals-wizard.js L55–115). The prototype merges client picker + project
 * fields into a single pane; we follow that contract.
 *
 *   - `mode: 'existing'` → pick from the firm-wide client list.
 *   - `mode: 'new'`      → fill the new-client form. The wizard runs
 *                          `POST /clients` lazily on the Continue-from-Modules
 *                          step (the engagement is minted as part of that
 *                          flow); see commit timing notes above.
 */
export interface ClientStepState {
  mode: 'existing' | 'new';
  existingClientId: string;
  newClient: {
    name: string;
    client_type: string;
    primary_contact_name: string;
    primary_contact_email: string;
  };
}

export interface ProjectStepState {
  name: string;
  /** ISO date string (YYYY-MM-DD). Optional. */
  start_date: string;
  /** Target go-live. ISO date string. Optional. */
  planned_end: string;
  /** Client-side PM name + title; persists on Project.client_pm (T-0128). */
  client_pm: string;
  /**
   * Engagement lead user id. Captured in wizard state so the rail-summary
   * and confirm view echo it; not currently surfaced on the create-project
   * payload (no `lead_user_id` column on Project yet). Future ticket. The
   * prototype defaults this to 'mk' (M. Kapoor) — we mirror that token but
   * the value is informational until the backend grows the column.
   */
  lead_user_id: string;
}

export interface ModulesStepState {
  contract: boolean;
  health: boolean;
  golive: boolean;
  /** Selection (E11): requirements now, vendor evaluation later. */
  selection: boolean;
  /**
   * Discovery (E12). Turning it on turns Selection on: the API sets
   * `selection` with it (T-0171), so the wizard sends only what was ticked
   * and shows Selection ticked and locked while this is on.
   */
  discovery: boolean;
}

/**
 * Step 3 (Baseline docs) tracks document upload state per file. The
 * actual document rows live on the backend post-engagement-create; this
 * client-side mirror feeds the upload row UI (progress bar + parse chip).
 *
 * Each entry corresponds to one user-initiated upload that the wizard
 * pushed through T-0040 `uploadFileChunked`. After completion the
 * `document_id` is populated and the step polls `listEngagementDocuments`
 * to derive parse / index status.
 */
export interface BaselineDocFileState {
  /** Stable id minted client-side per add so React keys are stable across
   *  progress ticks. */
  client_id: string;
  name: string;
  size_bytes: number;
  /** Set after the chunked-upload completes; null until then. */
  document_id: string | null;
  /** 0..100 — derived from chunk progress; jumps to 100 on completeUpload. */
  progress: number;
  /** Status driven by upload + (post-create) backend `process_status`. */
  status: 'uploading' | 'pending' | 'extracted' | 'indexed' | 'failed';
  /**
   * T-0159: the pipeline stage from the documents list (uploading | reading
   * | indexing | ready | failed). Absent until the list has the document, or
   * from an older server. Step 4 waits for every document to be ready or
   * failed before it starts the AI pass (see `documentReadiness`).
   */
  stage?: 'uploading' | 'reading' | 'indexing' | 'ready' | 'failed';
  /** Last error message from upload — surfaced as a red status chip. */
  error: string | null;
}

export interface BaselineDocsStepState {
  files: BaselineDocFileState[];
  /**
   * Whose documents these are (E19 / T-0215): the step's "Whose document is
   * this?" answer, Vendor until changed, sent on every initiate.
   */
  party: DocumentParty;
}

/**
 * Step 4 (Base formation) — driven by `POST /engagements/:id/base-formation/run`
 * (T-0047). Once started the run row id is captured so the step can poll
 * `GET .../base-formation/runs/:run_id` until the status is terminal:
 * `succeeded`, `degraded` (some sections skipped, what was read kept) or
 * `failed`. The step keeps the three apart so it can say which one it was
 * (T-0228: never a green tick over an empty or failed run).
 */
export interface BaseFormationStepState {
  /** Backend run id once `POST .../base-formation/run` returns. Empty
   *  string before the run kicks off (e.g., when the engagement was just
   *  minted and the wizard is about to call /run). */
  run_id: string;
  /** Last-seen status. 'idle' before /run returns, then mirrors backend
   *  ('queued' | 'running' | 'succeeded' | 'degraded' | 'failed'). */
  status: 'idle' | 'queued' | 'running' | 'succeeded' | 'degraded' | 'failed';
  proposal_count: number;
  /** Failure code if the run did not succeed. Worded by the step. */
  failure_reason: string | null;
}

/** The three ways a run ends. */
export function isTerminalRunStatus(status: BaseFormationStepState['status']): boolean {
  return status === 'succeeded' || status === 'degraded' || status === 'failed';
}

/**
 * Whether step 4 may start the AI pass, from the wizard's upload rows
 * (T-0228, C2-07). The rows carry each stored document's pipeline stage
 * from the documents list, which both step 3 and step 4 poll.
 *
 * Counted as "to read" (`total`): every upload still in flight (it has no
 * document id yet — the production run that read nothing started while
 * the SOW was still uploading) and every stored document. A refused or
 * failed upload never counts: nothing reached the server. `indexed` is how
 * many stored documents the server reports ready for search. `ready` is
 * true once nothing is in flight and every stored document is ready or
 * failed; the run starts only then, and only when at least one document
 * is indexed (`indexed > 0`).
 */
export function documentReadiness(files: ReadonlyArray<BaselineDocFileState>): {
  total: number;
  indexed: number;
  pending: number;
  ready: boolean;
} {
  let inFlight = 0;
  let stored = 0;
  let indexed = 0;
  let pending = 0;
  for (const f of files) {
    if (!f.document_id) {
      if (f.status === 'uploading') inFlight += 1;
      continue;
    }
    stored += 1;
    const stage = stageFrom(f.stage, f.status);
    if (stage === 'ready') indexed += 1;
    else if (stage !== 'failed') pending += 1;
  }
  return {
    total: inFlight + stored,
    indexed,
    pending: inFlight + pending,
    ready: inFlight === 0 && pending === 0,
  };
}

/** The pipeline stage, from the stage field or (older servers) the process status. */
function stageFrom(
  stage: BaselineDocFileState['stage'] | undefined,
  status: string,
): NonNullable<BaselineDocFileState['stage']> {
  if (stage) return stage;
  switch (status) {
    case 'indexed':
      return 'ready';
    case 'failed':
      return 'failed';
    case 'extracted':
      return 'indexing';
    case 'uploading':
      return 'uploading';
    default:
      return 'reading';
  }
}

export interface NewEngagementWizardState {
  step: WizardStepId;
  client: ClientStepState;
  project: ProjectStepState;
  modules: ModulesStepState;
  baselineDocs: BaselineDocsStepState;
  baseFormation: BaseFormationStepState;
  /**
   * Once the engagement is minted (end of step 2 → Continue), this carries
   * the live ids. Steps 3/4 read from here. Null when the wizard is on
   * step 1 or 2 (or returning to those steps).
   */
  engagement: {
    engagement_id: string;
    project_id: string;
    client_id: string;
  } | null;
}

/**
 * Default state. The wizard re-seeds from this on every open so a Cancel
 * → Open cycle doesn't preserve stale form values.
 *
 * `preselectClientId` lets callers (e.g. the client-detail "+ New
 * engagement" CTA) deep-link in with the client already selected.
 */
export function emptyWizardState(preselectClientId?: string): NewEngagementWizardState {
  return {
    step: 'client-project',
    client: {
      mode: 'existing',
      existingClientId: preselectClientId ?? '',
      newClient: {
        name: '',
        client_type: 'city',
        primary_contact_name: '',
        primary_contact_email: '',
      },
    },
    project: {
      name: '',
      start_date: '',
      planned_end: '',
      client_pm: '',
      lead_user_id: '',
    },
    modules: {
      contract: true,
      health: true,
      golive: false,
      selection: false,
      discovery: false,
    },
    baselineDocs: {
      files: [],
      party: 'vendor',
    },
    baseFormation: {
      run_id: '',
      status: 'idle',
      proposal_count: 0,
      failure_reason: null,
    },
    engagement: null,
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Per-step gate: returns true when the step is internally consistent
 * enough to advance. The wizard footer's Continue button reads this; if
 * false the Continue is disabled and the step pane surfaces inline
 * validation hints next to the offending field.
 */
export function isStepValid(state: NewEngagementWizardState, step: WizardStepId): boolean {
  switch (step) {
    case 'client-project': {
      const clientOk =
        state.client.mode === 'existing'
          ? state.client.existingClientId.length > 0
          : state.client.newClient.name.trim().length > 0 &&
            state.client.newClient.client_type.length > 0 &&
            state.client.newClient.primary_contact_name.trim().length > 0 &&
            EMAIL_RE.test(state.client.newClient.primary_contact_email);
      return clientOk && state.project.name.trim().length > 0;
    }
    case 'modules':
      return (
        state.modules.contract ||
        state.modules.health ||
        state.modules.golive ||
        state.modules.selection ||
        state.modules.discovery
      );
    case 'next-steps':
      // The closing step of the no-baseline flow only points the way;
      // the engagement already exists, so Open engagement is always on.
      return true;
    case 'baseline-docs':
      // Optional step — Continue is enabled regardless. Failed uploads
      // are surfaced inline but don't block.
      return true;
    case 'base-formation': {
      // The last step: Finish once the run has ended (succeeded, degraded
      // or failed — the wizard isn't gated on AI succeeding), or when there
      // is nothing to read and so no run to wait for. While documents are
      // still uploading or indexing, or the run is going, the user waits.
      if (isTerminalRunStatus(state.baseFormation.status)) return true;
      if (state.baseFormation.status !== 'idle') return false;
      const docs = documentReadiness(state.baselineDocs.files);
      return docs.ready && docs.indexed === 0;
    }
    default:
      return false;
  }
}

/** Step-by-id lookup within the flow these modules run, for the rail. */
export function stepIndex(step: WizardStepId, modules: ModulesStepState): number {
  return wizardStepsFor(modules).findIndex((s) => s.id === step);
}

/** Move forward / backward within the bounds of the flow these modules run. */
export function nextStep(step: WizardStepId, modules: ModulesStepState): WizardStepId {
  const steps = wizardStepsFor(modules);
  const i = stepIndex(step, modules);
  return steps[Math.min(i + 1, steps.length - 1)]!.id;
}
export function prevStep(step: WizardStepId, modules: ModulesStepState): WizardStepId {
  const steps = wizardStepsFor(modules);
  const i = stepIndex(step, modules);
  return steps[Math.max(i - 1, 0)]!.id;
}

/**
 * Dirty-state: true if the user has changed anything from what the wizard
 * opened with *and* the engagement hasn't been minted yet. Once the
 * engagement is live (post-step 2), every later step decision lives on a
 * backend row, so there is nothing left to keep as a draft.
 *
 * `opened` is the untouched form for this open: the empty form, carrying
 * the preselected client when the wizard was opened from one (the
 * sidebar's client filter, the client page). Measured against the plain
 * empty form, a wizard opened with a client preselected was dirty before
 * anything was typed, and closing it saved an "Untitled engagement" draft
 * every time. A resumed draft is measured against the plain empty form, so
 * it is saved once more on close, as before.
 */
export function isWizardDirty(
  state: NewEngagementWizardState,
  opened: NewEngagementWizardState = emptyWizardState(),
): boolean {
  // Engagement already minted → no draft; the row exists on the server,
  // closing the wizard just navigates away from it.
  if (state.engagement) return false;
  const a = state;
  const b = opened;
  return (
    a.client.mode !== b.client.mode ||
    a.client.existingClientId !== b.client.existingClientId ||
    a.client.newClient.name !== b.client.newClient.name ||
    a.client.newClient.client_type !== b.client.newClient.client_type ||
    a.client.newClient.primary_contact_name !== b.client.newClient.primary_contact_name ||
    a.client.newClient.primary_contact_email !== b.client.newClient.primary_contact_email ||
    a.project.name !== b.project.name ||
    a.project.start_date !== b.project.start_date ||
    a.project.planned_end !== b.project.planned_end ||
    a.project.client_pm !== b.project.client_pm ||
    a.project.lead_user_id !== b.project.lead_user_id ||
    a.modules.contract !== b.modules.contract ||
    a.modules.health !== b.modules.health ||
    a.modules.golive !== b.modules.golive ||
    a.modules.selection !== b.modules.selection ||
    a.modules.discovery !== b.modules.discovery
  );
}

/**
 * The name a saved draft goes by in the portfolio's drafts banner: the
 * engagement name when one was typed, otherwise the client it is for (the
 * new client's name, or the chosen client's name from `clientNames`, keyed
 * by id), otherwise "Untitled engagement".
 */
export function engagementDraftLabel(
  draft: Partial<NewEngagementWizardState>,
  clientNames: ReadonlyMap<string, string>,
): string {
  const project = draft.project?.name?.trim();
  if (project) return project;
  const client = draft.client;
  const clientName =
    client?.mode === 'existing'
      ? clientNames.get(client.existingClientId)?.trim()
      : client?.newClient?.name?.trim();
  return clientName ? `Draft for ${clientName}` : 'Untitled engagement';
}
